import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Modal, ModalFooter } from './Modal';
import { MagnifyingGlassIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  groupApi,
  teamApi,
  userApi,
  viewApi,
  type Group,
  type Team,
  type User,
  type SavedView,
  type SavedFilterState,
  type ViewShareType,
} from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/contexts/AuthContext';

// Portal-based searchable multi-select — mirrors the OwnerSelect control used in the
// add/edit initiative form so share targets use a consistent, scalable input (works with
// hundreds of users/groups). Renders to document.body so it isn't clipped by the modal.
interface ShareOption {
  value: string;
  primary: string;
  secondary?: string;
  disabled?: boolean;
  disabledHint?: string;
}

function ShareMultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder,
}: {
  options: ShareOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number; maxListHeight: number }>(
    { left: 0, width: 0, maxListHeight: 224 }
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // The search header (input + padding) is a fixed ~49px; only the option list below it scrolls.
  const HEADER_HEIGHT = 49;
  const MIN_LIST_HEIGHT = 120;
  const MAX_LIST_HEIGHT = 224; // matches the old max-h-56

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    // Flip upward only when there's meaningfully more room above than below and below is tight —
    // otherwise keep the default downward placement so it doesn't flip on marginal differences.
    const openUpward = spaceBelow < MIN_LIST_HEIGHT + HEADER_HEIGHT && spaceAbove > spaceBelow;
    const available = (openUpward ? spaceAbove : spaceBelow) - HEADER_HEIGHT;
    const maxListHeight = Math.max(MIN_LIST_HEIGHT, Math.min(MAX_LIST_HEIGHT, available));
    setPos({
      top: openUpward ? undefined : rect.bottom + 4,
      bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
      left: rect.left,
      width: rect.width,
      maxListHeight,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setIsOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setTimeout(() => searchRef.current?.focus(), 0);
  }, [isOpen]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.primary.toLowerCase().includes(q) || (o.secondary?.toLowerCase().includes(q) ?? false))
    : options;

  const toggle = (v: string) => {
    const opt = options.find((o) => o.value === v);
    if (opt?.disabled) return;
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const triggerText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.primary ?? '1 selected'
        : `${selected.length} selected`;

  const dropdown = isOpen && createPortal(
    <div
      ref={dropdownRef}
      className="bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
      style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, zIndex: 9999 }}
    >
      <div className="p-2 border-b border-gray-100">
        <div className="flex items-center gap-2 px-2 py-1 border border-gray-200 rounded text-sm focus-within:border-blue-500">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>
      </div>
      <div className="overflow-auto" style={{ maxHeight: pos.maxListHeight }}>
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-400 text-center">No results</div>
        ) : (
          filtered.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                disabled={opt.disabled}
                title={opt.disabled ? opt.disabledHint : undefined}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${
                  opt.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'bg-blue-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}
                >
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate ${isSelected ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                    {opt.primary}
                    {opt.disabled && opt.disabledHint && (
                      <span className="ml-1 text-xs text-gray-400 font-normal">({opt.disabledHint})</span>
                    )}
                  </span>
                  {opt.secondary && (
                    <span className="block text-xs text-gray-400 truncate" title={opt.secondary}>
                      {opt.secondary}
                    </span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-md border border-border-default px-3 py-2 text-sm bg-surface-default text-left cursor-pointer focus:border-border-focus focus:ring-2 focus:ring-border-focus/20"
      >
        <span className={`truncate ${selected.length > 0 ? 'text-text-primary' : 'text-gray-400'}`}>{triggerText}</span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {dropdown}
    </>
  );
}

interface SaveViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  viewType: 'timeline' | 'list' | 'kanban' | 'calendar';
  filterState: SavedFilterState;
  /** When set, the modal edits this view's title/sharing instead of creating a new one. */
  existingView?: SavedView | null;
  onSaved: (view: SavedView) => void;
}

const SHARE_OPTIONS: { value: ViewShareType; label: string; help: string }[] = [
  { value: 'ONLY_ME', label: 'Only me', help: 'Private — only you can see this view' },
  { value: 'EVERYONE', label: 'Everyone', help: 'Share with everyone in your organisation' },
  { value: 'GROUP', label: 'Groups', help: 'Share with one or more groups' },
  { value: 'TEAM', label: 'Teams', help: 'Share with one or more teams' },
  { value: 'USER', label: 'Users', help: 'Share with specific people' },
];

export function SaveViewModal({ isOpen, onClose, viewType, filterState, existingView, onSaved }: SaveViewModalProps) {
  const notify = useAppStore((s) => s.notify);
  const accessibleViews = useAppStore((s) => s.accessibleViews);
  const { user: currentUser } = useAuth();
  const isEdit = !!existingView;

  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [shareType, setShareType] = useState<ViewShareType>('ONLY_ME');
  const [targetIds, setTargetIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const [groups, setGroups] = useState<Group[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Reset + prefill when opened
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setTitle(existingView?.title ?? '');
    setTitleError(null);
    setShareType(existingView?.shareType ?? 'ONLY_ME');
    setTargetIds(existingView?.targets.map((t) => t.id) ?? []);
    Promise.all([groupApi.getAll(), teamApi.getAll(), userApi.getAll()])
      .then(([g, t, u]) => {
        setGroups(g);
        setTeams(t);
        setUsers(u);
      })
      .catch(() => notify('error', 'Failed to load sharing options'));
  }, [isOpen, existingView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selected targets when switching category
  const changeShareType = (next: ViewShareType) => {
    setShareType(next);
    setTargetIds([]);
  };

  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const shareOptions = useMemo<ShareOption[]>(() => {
    if (shareType === 'GROUP') {
      return groups.map((g) => ({ value: g.id, primary: g.name }));
    }
    if (shareType === 'TEAM') {
      return teams.map((t) => ({ value: t.id, primary: t.name, secondary: t.group?.name }));
    }
    if (shareType === 'USER') {
      return users.map((u) => {
        const team = u.teamId ? teamById.get(u.teamId) : undefined;
        const parts = [u.email];
        if (team?.name) parts.push(team.name);
        if (team?.group?.name) parts.push(team.group.name);
        return {
          value: u.id,
          primary: u.name,
          secondary: parts.join(' · '),
          disabled: u.id === currentUser?.id,
          disabledHint: u.id === currentUser?.id ? 'owner' : undefined,
        };
      });
    }
    return [];
  }, [shareType, groups, teams, users, teamById, currentUser?.id]);

  const needsTargets = shareType !== 'ONLY_ME' && shareType !== 'EVERYONE';
  const canProceed = title.trim().length >= 1;
  const canSave = canProceed && (!needsTargets || targetIds.length > 0);

  // Instant client-side duplicate check (same rule the server enforces) so the user finds out
  // before stepping into Share, rather than after — uses already-loaded accessibleViews, which
  // always includes the current user's own views regardless of role (see GET /views).
  const findDuplicateTitle = (): string | null => {
    const trimmed = title.trim();
    const dup = accessibleViews.some(
      (v) =>
        v.viewType === viewType &&
        v.ownerId === currentUser?.id &&
        v.id !== existingView?.id &&
        v.title.trim().toLowerCase() === trimmed.toLowerCase()
    );
    return dup ? `You already have a "${trimmed}" view in ${viewType}` : null;
  };

  const handleNext = () => {
    if (!canProceed) return;
    const dupError = findDuplicateTitle();
    if (dupError) {
      setTitleError(dupError);
      return;
    }
    setTitleError(null);
    setStep(2);
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        viewType,
        filterState,
        shareType,
        targetIds: needsTargets ? targetIds : [],
      };
      const view = isEdit
        ? await viewApi.update(existingView!.id, payload)
        : await viewApi.create(payload);
      onSaved(view);
      onClose();
    } catch (e) {
      // Surfaced inline on step 1 (below the title field) rather than as a toast — the toast
      // renders in the app header, behind this modal, so the user would never see it.
      setTitleError((e as { message?: string })?.message || 'Failed to save view');
      setStep(1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit view' : 'Save view'} size="medium">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-5 text-xs font-medium">
        <span className={step === 1 ? 'text-blue-600' : 'text-text-tertiary'}>1. Details</span>
        <span className="text-text-tertiary">›</span>
        <span className={step === 2 ? 'text-blue-600' : 'text-text-tertiary'}>2. Share</span>
      </div>

      {step === 1 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-primary">
            View title <span className="text-red-500">*</span>
          </label>
          <input
            autoFocus
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && canProceed && handleNext()}
            maxLength={200}
            placeholder="e.g. Q3 Blocked initiatives"
            className={`w-full rounded-md border bg-background-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none ${
              titleError ? 'border-red-400 focus:border-red-500' : 'border-border-default focus:border-blue-500'
            }`}
          />
          {titleError ? (
            <p className="text-xs text-red-600">{titleError}</p>
          ) : (
            <p className="text-xs text-text-tertiary">
              Saves the current filters on the {viewType} view.
            </p>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {SHARE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => changeShareType(opt.value)}
                className={`text-left px-3 py-2 rounded-md border transition-colors ${
                  shareType === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-border-default hover:border-border-focus'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                      shareType === opt.value ? 'border-blue-600' : 'border-border-default'
                    }`}
                  >
                    {shareType === opt.value && <span className="w-2 h-2 rounded-full bg-blue-600" />}
                  </span>
                  <span className="text-sm font-medium text-text-primary">{opt.label}</span>
                </div>
                <p className="text-xs text-text-tertiary mt-0.5 ml-5">{opt.help}</p>
              </button>
            ))}
          </div>

          {needsTargets && (
            <ShareMultiSelect
              options={shareOptions}
              selected={targetIds}
              onChange={setTargetIds}
              placeholder={`Select ${shareType === 'USER' ? 'people' : shareType.toLowerCase() + 's'}…`}
              searchPlaceholder={`Search ${shareType === 'USER' ? 'people' : shareType.toLowerCase() + 's'}…`}
            />
          )}
        </div>
      )}

      <ModalFooter>
        {step === 2 ? (
          <button
            onClick={() => setStep(1)}
            className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary"
          >
            Back
          </button>
        ) : (
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
        )}
        {step === 1 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save view'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
